/** KeyboardInput — Arrow keys / WASD で 8 方向移動を提供。
 *  Phase A2 Day 5 — 同時押し(W+D = upRight 等)で斜め方向を生成。
 */

import type { Direction } from '../core/types';
import { vectorToDirection } from '../core/types';

export interface KeyboardInputOptions {
  readonly onDirection: (dir: Direction) => void;
  /** C1: 全キーリリース時(ベクタが null)に呼ばれる「止める」コールバック(任意)。
   *  paper.io 仕様では走り続けるため通常は渡さない。 */
  readonly onStop?: () => void;
  /** C2: Escape / p キーで pause トグル(任意) */
  readonly onTogglePause?: () => void;
}

/** M2: 小文字キーのみ登録。keydown/keyup で event.key.toLowerCase() を使って引く。 */
const KEY_TO_AXIS: Record<string, { x: number; y: number }> = {
  arrowup: { x: 0, y: -1 },
  arrowdown: { x: 0, y: 1 },
  arrowleft: { x: -1, y: 0 },
  arrowright: { x: 1, y: 0 },
  w: { x: 0, y: -1 },
  s: { x: 0, y: 1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 },
};

export function attachKeyboardInput(options: KeyboardInputOptions): () => void {
  /** M2: pressed Set も小文字で統一。 */
  const pressed = new Set<string>();

  const recompute = (): void => {
    let dx = 0;
    let dy = 0;
    for (const key of pressed) {
      const axis = KEY_TO_AXIS[key];
      if (axis) {
        dx += axis.x;
        dy += axis.y;
      }
    }
    const dir = vectorToDirection(dx, dy);
    if (dir) {
      options.onDirection(dir);
    } else {
      /** C1: dir が null = 全キーリリース → onStop を呼ぶ。 */
      options.onStop?.();
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    // C2: Pause トグル(Escape / p / P)
    if (event.key === 'Escape' || event.key === 'p' || event.key === 'P') {
      if (options.onTogglePause) {
        options.onTogglePause();
        event.preventDefault();
      }
      return;
    }
    /** M2: toLowerCase() で大文字/小文字を統一して引く。 */
    const key = event.key.toLowerCase();
    if (!(key in KEY_TO_AXIS)) return;
    if (!pressed.has(key)) {
      pressed.add(key);
      recompute();
    }
    event.preventDefault();
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    /** M2: toLowerCase() で大文字/小文字を統一して削除する。 */
    const key = event.key.toLowerCase();
    if (!(key in KEY_TO_AXIS)) return;
    pressed.delete(key);
    recompute();
  };

  const onBlur = (): void => {
    pressed.clear();
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);

  return (): void => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    pressed.clear();
  };
}
